/**
 * Which lots may leave storage, by QC (user, 2026-09-29; 2026-09-30):
 *
 *  - Processing (Processing Slip, Packing Slip) and Stock Transfer Out take QC-passed lots. An
 *    Under Testing lot goes only on a Soft Release or Super Soft Release (seedtrac's SR / SSR,
 *    `qc_soft_releases`), which covers the lots made from it too. There is no other approval.
 *
 * Everything else is on hold: Rejected, Quarantined, Expired, Pending Review, and a lot that needs
 * QC but has no live sample (`not_applicable` with `qaRequired`). A lot that needs no QC at all
 * (`qaRequired` false, e.g. a cob lot) is free to move. A lot the Warehouse desk put on Quarantine
 * (Seed Status Q) is on hold whatever its QC says.
 *
 * Drying is not gated: it comes before the lab. Lot Blending takes the same lots as processing
 * (user, 2026-10-13, matching seedtrac's live Lot Blending picker): QC-passed, or Under Testing on a
 * Soft / Super Soft Release -- and never a lot whose GOT result is Fail (`lots/holds.ts`).
 */
import type { LotQcStatus } from "./qc";

export const QC_PASSED: readonly LotQcStatus[] = ["eligible", "released"];

/** The QC states a lot may be picked in at all: Under Testing only with a release. */
export const QC_PICKABLE: readonly LotQcStatus[] = [
  ...QC_PASSED,
  "under_testing",
];

export type LotHoldUse = "processing" | "transfer" | "blend";

export interface LotHoldSubject {
  lotNumber: string;
  qaRequired: boolean;
  qcStatus: string;
  seedStatuses?: readonly string[] | null;
  /** On an active Soft / Super Soft Release (its own, or of the lot it was made from). */
  softReleased?: boolean;
}

export type LotHold = { kind: "free" } | { kind: "held"; message: string };

const QC_LABEL: Record<string, string> = {
  under_testing: "Under Testing",
  expired: "QC Expired",
  pending_review: "QC Pending Review",
  rejected: "QC Rejected",
  quarantined: "Quarantined",
  not_applicable: "not QC tested",
};

export function lotHold(l: LotHoldSubject, use: LotHoldUse): LotHold {
  if (l.seedStatuses?.includes("quarantine"))
    return {
      kind: "held",
      message: `Lot ${l.lotNumber} is on Quarantine at the Warehouse desk.`,
    };
  const qc = l.qcStatus as LotQcStatus;
  if (QC_PASSED.includes(qc)) return { kind: "free" };
  if (!l.qaRequired && qc === "not_applicable") return { kind: "free" };
  const label = QC_LABEL[qc] ?? qc;
  if (qc === "under_testing") {
    if (l.softReleased) return { kind: "free" };
    return {
      kind: "held",
      message: `Lot ${l.lotNumber} is ${label}; it can go only on a Soft Release or Super Soft Release.`,
    };
  }
  const allowed = "QC-passed or soft-released";
  const verb = use === "blend" ? "be blended" : "go";
  return {
    kind: "held",
    message: `Lot ${l.lotNumber} is ${label}; only ${allowed} lots can ${verb}.`,
  };
}
